import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { getAdminUsers } from "../../api/admin.api";
import { isUnauthorizedError } from "../../api/http";
import AdminBreadcrumbs from "../../components/admin/AdminBreadcrumbs";
import EmptyState from "../../components/ui/EmptyState";
import ErrorState from "../../components/ui/ErrorState";
import LoadingState from "../../components/ui/LoadingState";
import AdminLayout from "../../layouts/AdminLayout";
import { handleTableKeyNav } from "../../utils/tableNav";
import normalizeApiError from "../../utils/normalizeApiError";

const formatTimestamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const pad = (num) => String(num).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const statusTone = (value) =>
  value ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const canGoPrev = page > 1;
  const canGoNext = total ? page * pageSize < total : users.length === pageSize;

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAdminUsers({ page, pageSize });
      setUsers(response?.items || []);
      setTotal(response?.total ?? null);
      setPage(response?.page || page);
    } catch (err) {
      if (isUnauthorizedError(err)) {
        setError(normalizeApiError(err));
        return;
      }
      setError(normalizeApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, pageSize]);

  const displayUsers = useMemo(() => users, [users]);

  return (
    <AdminLayout title="Admin Users">
      <div className="space-y-6">
        <div className="card p-4">
          <AdminBreadcrumbs />
        </div>
        {loading ? (
          <LoadingState variant="table" rows={6} />
        ) : error ? (
          <ErrorState error={error} onRetry={fetchUsers} />
        ) : displayUsers.length === 0 ? (
          <EmptyState
            title="No users found"
            message="No users are available for this page."
            testId="admin-empty-users"
          />
        ) : (
          <>
            <div
              className="hidden overflow-hidden rounded-lg border border-slate-200 lg:block"
              onKeyDown={handleTableKeyNav}
            >
          <table
            className="w-full text-left text-sm"
            role="table"
            aria-label="Admin users"
          >
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">ID</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Phone</th>
                    <th className="px-4 py-3">Verified</th>
                    <th className="px-4 py-3">Active</th>
                    <th className="px-4 py-3">Roles</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayUsers.map((user) => (
                    <tr
                      key={user.id}
                      className="border-t border-slate-100 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-amber-400"
                      tabIndex={0}
                      data-row="true"
                    >
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {user.id || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {user.email || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {user.phone || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-semibold ${statusTone(
                            user.isVerified
                          )}`}
                        >
                          {user.isVerified ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-semibold ${statusTone(
                            user.isActive
                          )}`}
                        >
                          {user.isActive ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {Array.isArray(user.roles) ? user.roles.join(", ") : "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {formatTimestamp(user.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {user.id ? (
                          <NavLink
                            className="text-amber-600"
                            to={`/admin/users/${user.id}`}
                          >
                            View detail
                          </NavLink>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="space-y-3 lg:hidden">
              {displayUsers.map((user) => (
                <div key={user.id} className="card p-4 text-xs text-slate-600">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-semibold text-slate-700">
                      {user.email || "Unknown user"}
                    </div>
                    <span
                      className={`rounded-full px-2 py-1 text-[10px] font-semibold ${statusTone(
                        user.isActive
                      )}`}
                    >
                      {user.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="mt-2 grid gap-2 text-slate-600">
                    <div>ID: {user.id || "N/A"}</div>
                    <div>Phone: {user.phone || "N/A"}</div>
                    <div>
                      Roles:{" "}
                      {Array.isArray(user.roles) ? user.roles.join(", ") : "N/A"}
                    </div>
                    <div>Created: {formatTimestamp(user.createdAt)}</div>
                  </div>
                  {user.id && (
                    <NavLink
                      className="mt-2 inline-block text-xs text-amber-600"
                      to={`/admin/users/${user.id}`}
                    >
                      View detail
                    </NavLink>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
        <div className="flex gap-2">
          <button
            className="btn btn-secondary"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={!canGoPrev || loading}
          >
            Prev
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={!canGoNext || loading}
          >
            Next
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
